import { useState } from 'react'
import { ArrowDown, ArrowUp, CloudOff, Loader2, WifiOff, ShieldAlert, RefreshCw } from 'lucide-react'
import { useApiStatus } from '@/hooks/use-api-status'
import { useSessionCountdown } from '@/hooks/use-session-countdown'
import { cn } from '@/lib/utils'
import { SyncStatusPanel } from '@/components/common/SyncStatusPanel'

type PingTier = 'good' | 'slow' | 'down' | 'offline-cached'

function pingTier(avgMs: number | null, connected: boolean, lastCacheHit: boolean): PingTier {
  if (!connected && lastCacheHit) return 'offline-cached'
  if (!connected) return 'down'
  if (avgMs === null) return 'down'
  if (avgMs >= 1000) return 'slow'
  if (avgMs >= 200) return 'slow'
  return 'good'
}

const tierColors: Record<PingTier, string> = {
  good: 'bg-emerald-500',
  slow: 'bg-amber-500',
  down: 'bg-red-500',
  'offline-cached': 'bg-amber-500',
}

const tierTextColors: Record<PingTier, string> = {
  good: 'text-emerald-600 dark:text-emerald-400',
  slow: 'text-amber-600 dark:text-amber-400',
  down: 'text-red-600 dark:text-red-400',
  'offline-cached': 'text-amber-600 dark:text-amber-400',
}

const tierLabels: Record<PingTier, string> = {
  good: 'Connected',
  slow: 'Unstable connection',
  down: 'Disconnected',
  'offline-cached': 'Offline (cached)',
}

export function ApiStatusBar() {
  const status = useApiStatus()
  const { connected, avgPingMs, activeReads, activeWrites, pendingSync, syncing, lastCacheHit, sessionDead, readOnly, cableReconnecting } = status
  const tier = pingTier(avgPingMs, connected, lastCacheHit)
  const [showSyncPanel, setShowSyncPanel] = useState(false)
  const { remainingLabel, expired, nearExpiry } = useSessionCountdown()

  const isOffline = !connected
  const showOfflineIndicator = isOffline && !sessionDead
  const showSessionDead = sessionDead && connected

  return (
    <div className="relative">
      <div className="flex items-center gap-3 rounded-md border border-border/50 bg-muted/30 px-3 py-1.5">
        {/* Heartbeat dot + label */}
        <div className="flex items-center gap-2">
          <span className="relative flex items-center justify-center">
            <span className={cn('h-2 w-2 rounded-full', tierColors[tier])} />
            {(tier === 'slow' || tier === 'down' || tier === 'offline-cached') && (
              <span
                className={cn(
                  'absolute h-2 w-2 rounded-full opacity-40 animate-ping',
                  tierColors[tier],
                )}
                style={{ animationDuration: '2s' }}
              />
            )}
          </span>
          <span className={cn('text-[11px] font-medium', tierTextColors[tier])}>
            {tierLabels[tier]}
          </span>
        </div>

        {/* Cable reconnecting indicator */}
        {cableReconnecting && (
          <>
            <span className="h-3 w-px bg-border/60" />
            <div className="flex items-center gap-1.5">
              <RefreshCw className="h-3 w-3 animate-spin text-amber-500" />
              <span className="text-[11px] font-medium text-amber-600 dark:text-amber-400">
                Reconnecting…
              </span>
            </div>
          </>
        )}

        {/* Offline session indicator - permanent, not dismissable */}
        {showOfflineIndicator && (
          <>
            <span className="h-3 w-px bg-border/60" />
            <div className="flex items-center gap-1.5">
              <WifiOff className={cn('h-3 w-3', expired || nearExpiry ? 'text-red-500' : 'text-amber-500')} />
              {expired || readOnly ? (
                <span className="text-[11px] font-medium text-red-600 dark:text-red-400">
                  Reconnect to the internet
                </span>
              ) : (
                <span className={cn(
                  'text-[11px] font-medium tabular-nums',
                  nearExpiry ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400',
                )}>
                  Session: {remainingLabel}
                </span>
              )}
            </div>
          </>
        )}

        {/* Session dead indicator (online but need re-auth) */}
        {showSessionDead && (
          <>
            <span className="h-3 w-px bg-border/60" />
            <div className="flex items-center gap-1.5">
              <ShieldAlert className="h-3 w-3 text-red-500" />
              <span className="text-[11px] font-medium text-red-600 dark:text-red-400">
                Sign in required
              </span>
            </div>
          </>
        )}

        {/* Separator */}
        <span className="h-3 w-px bg-border/60" />

        {/* Activity arrows */}
        <div className="flex items-center gap-1">
          <ArrowDown
            className={cn(
              'h-3 w-3 transition-colors',
              activeReads > 0 ? 'text-blue-500 animate-pulse' : 'text-muted-foreground/30',
            )}
          />
          <ArrowUp
            className={cn(
              'h-3 w-3 transition-colors',
              activeWrites > 0 ? 'text-orange-500 animate-pulse' : 'text-muted-foreground/30',
            )}
          />
        </div>

        {/* Sync status - shown when syncing or pending */}
        {(syncing || pendingSync > 0) && (
          <>
            <span className="h-3 w-px bg-border/60" />
            <button
              type="button"
              onClick={() => setShowSyncPanel(!showSyncPanel)}
              className="flex items-center gap-1.5 text-[11px] font-medium transition-colors hover:text-foreground"
            >
              {syncing ? (
                <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
              ) : (
                <CloudOff className="h-3 w-3 text-amber-500" />
              )}
              <span className={syncing ? 'text-blue-600 dark:text-blue-400' : 'text-amber-600 dark:text-amber-400'}>
                {syncing ? `Syncing ${pendingSync}…` : `${pendingSync} pending`}
              </span>
            </button>
          </>
        )}
      </div>

      {/* Sync status panel overlay */}
      {showSyncPanel && (
        <SyncStatusPanel onClose={() => setShowSyncPanel(false)} />
      )}
    </div>
  )
}
